'use client'

import React from 'react'
import { Section } from '@/components/Section'
import { Callout } from '@/components/Callout'
import { Text } from '@/components/ui/text'
import { Eyebrow } from '@/components/Eyebrow'
import { GlobalStats, GitHubActivity } from './types'
import { Subheading } from '@/components/ui/subheading'
import Link from 'next/link'
import { LinkButton } from '@/components/ui/LinkButton'

export function GitHubActivityClient({
  stats,
  activities,
}: {
  stats: GlobalStats
  activities: GitHubActivity[]
}) {
  return (
    <Section bgClassName="bg-paper-1" className="py-16">
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {/* Left column with heading, text and stats */}
          <div className="lg:w-1/3">
            <Callout
              variant="dark"
              eyebrow={null}
              heading="GitHub Activity"
              body="See what the Sia Foundation team and developer community are building."
            >
              <LinkButton
                href="https://github.com/SiaFoundation"
                // href="https://github.com/search?q=org%3ASiaFoundation+is%3Apr+-author%3Adependabot%5Bbot%5D&type=pullrequests"
                newTab
                variant="olive"
                linkIcon="external"
                className="mt-6"
              >
                View full activity feed
              </LinkButton>

              {/* Stats box */}
              <div className="mt-10 bg-powder p-8">
                <Eyebrow variant="dark" label="DEVELOPER STATS" color="punch" />

                <div className="grid grid-cols-2 gap-x-6 gap-y-8 mt-8">
                  <div>
                    <Text size="xl" className="block font-heading text-olive-2 leading-none">
                      {stats.commits.toLocaleString()}
                    </Text>
                    <Text size="sm" className="block text-olive-2 mt-1">
                      commits
                    </Text>
                  </div>

                  <div>
                    <Text size="xl" className="block font-heading text-olive-2 leading-none">
                      {stats.contributors.toLocaleString()}
                    </Text>
                    <Text size="sm" className="block text-olive-2 mt-1">
                      contributors
                    </Text>
                  </div>

                  <div>
                    <Text size="xl" className="block font-heading text-olive-2 leading-none">
                      {stats.forks.toLocaleString()}
                    </Text>
                    <Text size="sm" className="block text-olive-2 mt-1">
                      forks
                    </Text>
                  </div>

                  <div>
                    <Text size="xl" className="block font-heading text-olive-2 leading-none">
                      {stats.releases.toLocaleString()}
                    </Text>
                    <Text size="sm" className="block text-olive-2 mt-1">
                      releases
                    </Text>
                  </div>
                </div>
              </div>
            </Callout>
          </div>

          {/* Right column with activity list */}
          <div className="lg:w-2/3">
            <div className="space-y-8">
              {activities.map((activity, index) => (
                <div key={index} className="border-b border-rock-1 last:border-b-0 pb-8">
                  <div className="flex flex-col items-start gap-[20px]">
                    <Eyebrow variant="dark" label={activity.date} color="powder" size="sm" />
                    <div className="pl-[40px] flex flex-col items-start gap-[20px]">
                      <Link
                        href={`https://github.com/${activity.repo}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center bg-rock-1 px-[10px] py-[6px] gap-1 hover:underline hover:bg-highlighter"
                      >
                        <GitHubIcon />
                        <Subheading size="xs">{activity.repo}</Subheading>
                      </Link>
                      <Link
                        href={`https://github.com/${activity.repo}/issues/${activity.number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        <Subheading size="sm">
                          #{activity.number} {activity.type}: {activity.title}
                        </Subheading>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}

function GitHubIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 0C5.374 0 0 5.373 0 12C0 17.302 3.438 21.8 8.207 23.387C8.806 23.498 9 23.126 9 22.81V20.576C5.662 21.302 4.967 19.16 4.967 19.16C4.421 17.773 3.634 17.404 3.634 17.404C2.545 16.659 3.717 16.675 3.717 16.675C4.922 16.759 5.556 17.912 5.556 17.912C6.626 19.746 8.363 19.216 9.048 18.909C9.155 18.134 9.466 17.604 9.81 17.305C7.145 17 4.343 15.971 4.343 11.374C4.343 10.063 4.812 8.993 5.579 8.153C5.455 7.85 5.044 6.629 5.696 4.977C5.696 4.977 6.704 4.655 8.997 6.207C9.954 5.941 10.98 5.808 12 5.803C13.02 5.808 14.047 5.941 15.006 6.207C17.297 4.655 18.303 4.977 18.303 4.977C18.956 6.63 18.545 7.851 18.421 8.153C19.191 8.993 19.656 10.064 19.656 11.374C19.656 15.983 16.849 16.998 14.177 17.295C14.607 17.667 15 18.397 15 19.517V22.81C15 23.129 15.192 23.504 15.801 23.386C20.566 21.797 24 17.3 24 12C24 5.373 18.627 0 12 0Z"
        fill="currentColor"
      />
    </svg>
  )
}
